// app/src/lib/backup/exporter.js — full Dexie snapshot → gzip → AES-256-GCM → S3.
//
// Triggers:
//   - `visibilitychange` (app backgrounded) — scheduleAutoBackup() registers this.
//   - Explicit user action from Settings → "Backup now".
//
// Failure of a background backup must NEVER raise an unhandled rejection or interrupt
// foreground work. Errors are surfaced via the returned Promise and an `alerts` row.

import pako from 'pako';
import { db } from '../db.js';
import { deriveKeys, encrypt } from '../crypto.js';
import * as kv from '../idbKv.js';
import { getAccessToken, currentClaims } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';

const LAST_BACKUP_KEY = 'lastBackupAt'; // explicitly allow-listed in lint-no-localstorage.

export const BACKUP_TABLES = [
  'userProfile', 'vendors', 'invoices', 'lineItems',
  'salesEntries', 'recurringItems',
  'plaidItems', 'plaidAccounts', 'plaidTransactions',
  'deliveryStatements',
  'alerts',
];

/**
 * Build, encrypt, and upload a full snapshot. Throws on any failure.
 * @returns {Promise<{ uploadedAt: number, bytes: number }>}
 */
export async function backupNow() {
  const claims = await currentClaims();
  if (!claims) throw new Error('backup: not signed in');
  const userId = claims.sub;

  // 1. Collect every table.
  const snapshot = { version: 2, exportedAt: Date.now(), userId, tables: {} };
  for (const t of BACKUP_TABLES) {
    snapshot.tables[t] = await db.table(t).toArray();
  }

  // 2. Serialize → gzip.
  const json = new TextEncoder().encode(JSON.stringify(snapshot));
  const gz = pako.gzip(json);

  // 3. Encrypt. AAD binds the ciphertext to the userId — a recipient must know it
  //    to decrypt, and the GCM tag verifies it. Prevents cross-account replay if a
  //    bucket policy bug ever let an attacker substitute another user's blob.
  const { backupKey } = await deriveKeys(userId, kv);
  const aad = new TextEncoder().encode(`kanjo/backup/v2|${userId}`);
  const envelope = await encrypt(backupKey, gz, aad);

  // 4. PUT to S3 via presigned URL.
  const url = await getPresignedPut();
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': 'application/octet-stream' },
    body: envelope,
  });
  if (!res.ok) throw new Error(`backup: PUT failed ${res.status}`);

  const uploadedAt = Date.now();
  // lastBackupAt is the ONLY value in localStorage — it's non-sensitive and we want
  // it readable synchronously on app boot before IDB is open.
  localStorage.setItem(LAST_BACKUP_KEY, String(uploadedAt));
  return { uploadedAt, bytes: envelope.length };
}

/** Register a visibilitychange listener that backs up on app background. Returns a disposer. */
export function scheduleAutoBackup() {
  let inflight = false;
  const onChange = async () => {
    if (document.visibilityState !== 'hidden') return;
    if (inflight) return;
    inflight = true;
    try { await backupNow(); }
    catch (e) { console.warn('[backup] auto-backup failed', e); }
    finally { inflight = false; }
  };
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
}

export function getLastBackupAt() {
  const v = localStorage.getItem(LAST_BACKUP_KEY);
  return v ? Number(v) : 0;
}

async function getPresignedPut() {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('backup: no access token');
  const res = await fetch(`${cfg.apiBaseUrl}/backup/presign`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ kind: 'snapshot' }),
  });
  if (!res.ok) throw new Error(`backup: presign ${res.status}`);
  const body = await res.json();
  if (!body.url || typeof body.url !== 'string') throw new Error('backup: malformed presign');
  return body.url;
}
